"use client";

import { useQueryState } from "nuqs";
import { useCallback, useEffect, useState } from "react";
import type { BookmarkFilters } from "@/components/shared/filters/bookmark/bookmark-filter";
import { useCalendarPreferences } from "@/hooks/shared/use-calendar-preferences";
import {
	artistsSortParser,
	bookmarkFiltersParser,
	dayParser,
	festivalTabParser,
	genreParser,
	isHorizontalLayoutParser,
	myListSortParser,
	reviewFilterParser,
	sortAscendingParser,
	stageParser,
} from "@/lib/utils/url-parsers";

// Define default state values
const _DEFAULT_VIEW = "calendar"; // Will be overridden by user preference
const _DEFAULT_DAY = ""; // Will be set dynamically based on available days
const _DEFAULT_REVIEW_FILTER = "all";
const _DEFAULT_SORT_ASCENDING = false;
const _DEFAULT_MY_LIST_DAY = "all";
const _DEFAULT_MY_LIST_SORT = "time-asc";
const _DEFAULT_IS_HORIZONTAL = false;
const _DEFAULT_STAGE = "all";
const _DEFAULT_BOOKMARK_FILTERS: BookmarkFilters = {
	saved: false,
	rated: false,
	following: false,
	stream: false,
};

interface UseFestivalFiltersReturn {
	// Tab state
	activeTab: string;
	setActiveTab: (tab: string) => void;

	// Filter states
	selectedDay: string;
	setSelectedDay: (day: string) => void;
	selectedStage: string;
	setSelectedStage: (stage: string) => void;
	reviewFilter: string;
	setReviewFilter: (filter: string) => void;
	sortAscending: boolean;
	setSortAscending: (ascending: boolean) => void;
	sharedBookmarkFilters: BookmarkFilters;
	setSharedBookmarkFilters: (filters: BookmarkFilters) => void;
	myListDay: string;
	setMyListDay: (day: string) => void;
	myListSort: string;
	setMyListSort: (sort: string) => void;
	isHorizontalLayout: boolean;
	setIsHorizontalLayout: (horizontal: boolean) => void;

	// Search states
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	debouncedSearchTerm: string;
	selectedGenre: string;
	setSelectedGenre: (genre: string) => void;
	artistsSort: string;
	setArtistsSort: (sort: string) => void;

	// Handler functions
	handleTabChange: (tab: string) => void;
	handleDayChange: (day: string) => void;
	handleStageChange: (stageId: string) => void;
	handleGenreChange: (genre: string) => void;
	handleReviewFilterChange: (filter: string) => void;
	handleMyListDayChange: (day: string) => void;
	handleMyListSortChange: (sort: string) => void;
	handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleSortToggle: () => void;
	handleLayoutToggle: () => void;
	handleBookmarkFiltersChange: (filters: BookmarkFilters) => void;
	handleArtistsSortChange: (sort: string) => void;
}

/**
 * Hook for managing festival page UI state and filters
 *
 * Manages URL-synced state for:
 * - Tab navigation (calendar, list, map views)
 * - Day/stage filtering
 * - Review status filters
 * - Search and genre filtering
 * - Layout preferences (horizontal/vertical)
 *
 * @param _user - Current user (reserved for future user-specific defaults)
 * @returns Object with filter states, setters, and handler functions
 *
 * @example
 * ```tsx
 * const {
 *   activeTab,
 *   selectedDay,
 *   searchTerm,
 *   handleTabChange,
 *   handleDayChange
 * } = useFestivalFilters(user)
 * ```
 *
 * Note: For data fetching filter options, use hooks/features/festival/data/trpc/use-festival-filter-options.ts
 */
export function useFestivalFilters(
	_user: any | null,
): UseFestivalFiltersReturn {
	// Get user's calendar preferences including default festival view
	const { preferences: calendarPreferences } = useCalendarPreferences();

	// Use nuqs hooks for URL state management with user's preference as default
	const [activeTab, setActiveTab] = useQueryState(
		"view",
		festivalTabParser.withDefault(calendarPreferences.default_festival_view || _DEFAULT_VIEW)
	);
	const [selectedDay, setSelectedDay] = useQueryState("day", dayParser);
	const [selectedStage, setSelectedStage] = useQueryState("stage", stageParser);
	const [reviewFilter, setReviewFilter] = useQueryState(
		"filter",
		reviewFilterParser,
	);
	const [sortAscending, setSortAscending] = useQueryState(
		"sort",
		sortAscendingParser,
	);
	const [sharedBookmarkFilters, setSharedBookmarkFilters] = useQueryState(
		"bookmark",
		bookmarkFiltersParser,
	);
	const [myListDay, setMyListDay] = useQueryState(
		"myListDay",
		dayParser.withDefault("all"),
	);
	const [myListSort, setMyListSort] = useQueryState(
		"myListSort",
		myListSortParser,
	);
	const [isHorizontalLayout, setIsHorizontalLayout] = useQueryState(
		"horizontal",
		isHorizontalLayoutParser,
	);
	const [selectedGenre, setSelectedGenre] = useQueryState("genre", genreParser);
	const [artistsSort, setArtistsSort] = useQueryState(
		"artistsSort",
		artistsSortParser,
	);

	// Local state for search with debouncing
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

	// Debounce search input to prevent scroll jumps during typing
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Handler functions - nuqs handles URL updates automatically!
	const handleTabChange = useCallback(
		(tab: string) => {
			setActiveTab(tab);
		},
		[setActiveTab],
	);

	const handleDayChange = useCallback(
		(day: string) => {
			setSelectedDay(day);
		},
		[setSelectedDay],
	);

	const handleStageChange = useCallback(
		(stageId: string) => {
			setSelectedStage(stageId);
		},
		[setSelectedStage],
	);

	const handleGenreChange = useCallback(
		(genre: string) => {
			setSelectedGenre(genre);
		},
		[setSelectedGenre],
	);

	const handleReviewFilterChange = useCallback(
		(filter: string) => {
			setReviewFilter(filter);
		},
		[setReviewFilter],
	);

	const handleMyListDayChange = useCallback(
		(day: string) => {
			setMyListDay(day);
		},
		[setMyListDay],
	);

	const handleMyListSortChange = useCallback(
		(sort: string) => {
			setMyListSort(sort);
		},
		[setMyListSort],
	);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
		[],
	);

	const handleSortToggle = useCallback(() => {
		setSortAscending((prev) => !prev);
	}, [setSortAscending]);

	const handleLayoutToggle = useCallback(() => {
		setIsHorizontalLayout((prev) => !prev);
		if (!isHorizontalLayout) setSortAscending(true);
	}, [isHorizontalLayout, setIsHorizontalLayout, setSortAscending]);

	const handleBookmarkFiltersChange = useCallback(
		(filters: BookmarkFilters) => {
			setSharedBookmarkFilters(filters);
		},
		[setSharedBookmarkFilters],
	);

	const handleArtistsSortChange = useCallback(
		(sort: string) => {
			setArtistsSort(sort);
		},
		[setArtistsSort],
	);

	return {
		// Tab state
		activeTab,
		setActiveTab,

		// Filter states
		selectedDay,
		setSelectedDay,
		selectedStage,
		setSelectedStage,
		reviewFilter,
		setReviewFilter,
		sortAscending,
		setSortAscending,
		sharedBookmarkFilters,
		setSharedBookmarkFilters,
		myListDay,
		setMyListDay,
		myListSort,
		setMyListSort,
		isHorizontalLayout,
		setIsHorizontalLayout,

		// Search states
		searchTerm,
		setSearchTerm,
		debouncedSearchTerm,
		selectedGenre,
		setSelectedGenre,
		artistsSort,
		setArtistsSort,

		// Handler functions
		handleTabChange,
		handleDayChange,
		handleStageChange,
		handleGenreChange,
		handleReviewFilterChange,
		handleMyListDayChange,
		handleMyListSortChange,
		handleSearchChange,
		handleSortToggle,
		handleLayoutToggle,
		handleBookmarkFiltersChange,
		handleArtistsSortChange,
	};
}
